import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  format, addDays, differenceInCalendarDays, differenceInCalendarWeeks,
  isWeekend,
} from "date-fns";
import { useDiary } from "@/contexts/DiaryContext";
import {
  ChevronUp, ChevronDown, MoreHorizontal, Download, BookOpen, ClipboardList, CheckCircle2, ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useListTimetables, useGetTimetable, useListCourses, useListClasses,
  useGetDiaryEntries, useSaveDiarySlot, getGetDiaryEntriesQueryKey,
  useListDailyOverrides,
  customFetch,
} from "@workspace/api-client-react";
import { useLocation, useSearch } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { RichEditor, type InsertAPI } from "@/components/rich-editor";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useProgramRegisters, useDiaryProgramLinks, useAddDiaryProgramLink, type ProgramItem, type DiaryProgramLink } from "@/lib/program-registration";
import { ProgramItemPicker } from "@/components/program-registration/program-item-picker";
import {
  DeliveryStatusQuickLog,
  type PulledItem,
} from "@/components/program-registration/delivery-status-quick-log";
import { Skeleton } from "@/components/ui/skeleton";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const DAY_MAP: Record<number, string> = {
  1: "monday", 2: "tuesday", 3: "wednesday", 4: "thursday", 5: "friday",
};
const DAY_SHORT: Record<string, string> = {
  monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri",
};

function getDayName(date: Date): string {
  return DAY_MAP[date.getDay()] ?? "monday";
}

function dateLabel(date: Date, weekLabel: string, periodLabel: string): string {
  const day = DAY_SHORT[getDayName(date)] ?? "";
  return `${day} Week ${weekLabel}, ${periodLabel}`;
}

// Build a client-side HTML export for the visible filtered lessons
function buildExportHtml(
  entries: Array<{
    date: Date;
    weekLabel: string;
    slot: { label: string; courseId?: number | null; courseName?: string | null; courseColor?: string | null; className?: string | null };
    content: string;
  }>,
  timetableName: string,
  filterLabel: string | null,
): string {
  const filled = entries.filter((e) => e.content?.trim());
  if (filled.length === 0) return "<html><body><p>No lesson content to export.</p></body></html>";

  let body = "";
  let lastDate = "";
  for (const e of filled) {
    const d = format(e.date, "EEEE, d MMMM yyyy");
    if (d !== lastDate) {
      body += `<h2 class="day-heading">${d}</h2>`;
      lastDate = d;
    }
    const color = e.slot.courseColor ?? "#94a3b8";
    body += `
<div class="slot-block" style="border-left:4px solid ${color}">
  <div class="slot-header">
    <span class="slot-label">${e.slot.label}</span>
    ${e.slot.courseName ? `<span class="course-name" style="color:${color}">${e.slot.courseName}</span>` : ""}
    ${e.slot.className ? `<span class="class-name">${e.slot.className}</span>` : ""}
  </div>
  <div class="slot-content">${e.content}</div>
</div>`;
  }

  const title = `${timetableName}${filterLabel ? ` · ${filterLabel}` : ""} — Lesson Plans`;
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${title}</title>
<style>
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:13px;line-height:1.6;color:#1a1a1a;padding:32px}
h1.report-title{font-size:20px;font-weight:700;border-bottom:2px solid #e5e7eb;padding-bottom:12px;margin-bottom:24px}
h2.day-heading{font-size:14px;font-weight:700;background:#f3f4f6;border-radius:6px;padding:8px 12px;margin:20px 0 10px}
.slot-block{border-radius:6px;border:1px solid #e5e7eb;margin-bottom:10px;overflow:hidden;page-break-inside:avoid}
.slot-header{display:flex;align-items:center;gap:12px;padding:8px 14px;background:#fafafa;border-bottom:1px solid #e5e7eb}
.slot-label{font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.05em}
.course-name{font-size:13px;font-weight:700}
.class-name{font-size:11px;color:#6b7280;background:#f3f4f6;padding:1px 6px;border-radius:4px}
.slot-content{padding:12px 14px}
.slot-content img{max-width:100%;height:auto;border-radius:4px;margin:6px 0}
.slot-content ul,.slot-content ol{padding-left:20px}
.slot-content p{margin-bottom:4px}
.slot-content mark{background:#FFF176;padding:0 2px;border-radius:2px}
@media print{body{padding:0}.slot-block{page-break-inside:avoid}}
</style>
</head>
<body>
<h1 class="report-title">${title}</h1>
${body}
</body>
</html>`;
}

// ─── LessonEntry ─────────────────────────────────────────────────────────────

interface SlotShape {
  id: number;
  label: string;
  courseId?: number | null;
  courseName?: string | null;
  courseColor?: string | null;
  classId?: number | null;
  className?: string | null;
  position: number;
  // Task #142: surfaced from /timetables/{id} so the lesson list can flag
  // periods whose courseId / classId points at a hard-deleted row.
  courseMissing?: boolean;
  classMissing?: boolean;
}

interface LessonEntryProps {
  date: Date;
  weekLabel: string;
  slot: SlotShape;
  timetableId: number;
  initialContent: string;
  initialDiaryEntryId?: number | null;
  todayRef?: React.RefObject<HTMLDivElement | null>;
  onExportThis: (entry: { date: Date; weekLabel: string; slot: SlotShape; content: string }) => void;
}

function LessonEntry({ date, weekLabel, slot, timetableId, initialContent, initialDiaryEntryId, todayRef, onExportThis }: LessonEntryProps) {
  const [content, setContent] = useState(initialContent);
  const [saved, setSaved] = useState(false);
  const [collapsed, setCollapsed] = useState(!initialContent);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const insertRef = useRef<InsertAPI | null>(null);
  const [showProgramPicker, setShowProgramPicker] = useState(false);
  const [pendingInsertHtml, setPendingInsertHtml] = useState<string | null>(null);
  const [diaryEntryId, setDiaryEntryId] = useState<number | null>(initialDiaryEntryId ?? null);
  // Sync diaryEntryId from prop when diary entries finish loading (useState
  // doesn't re-initialize when props change after first mount).
  useEffect(() => {
    if (initialDiaryEntryId != null && diaryEntryId == null) {
      setDiaryEntryId(initialDiaryEntryId);
    }
  }, [initialDiaryEntryId]);
  const addLink = useAddDiaryProgramLink();
  const { data: persistedLinks = [] } = useDiaryProgramLinks(diaryEntryId);
  const pulledItems: PulledItem[] = persistedLinks.map((l: DiaryProgramLink) => ({
    registerId: l.programRegisterId,
    item: l.item,
    existingEvent: l.existingEvent ?? null,
  }));
  const loggedCount = persistedLinks.filter((l: DiaryProgramLink) => l.existingEvent !== null).length;
  const allDeliveryLogged = loggedCount > 0 && loggedCount === persistedLinks.length;
  const [showDeliveryStatus, setShowDeliveryStatus] = useState(false);
  const saveDiarySlot = useSaveDiarySlot();
  const queryClient = useQueryClient();
  const { data: allRegisters = [] } = useProgramRegisters();
  const hasActiveRegisters = allRegisters.some((r) => r.status !== "archived");
  const isToday = format(date, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");

  useEffect(() => { setContent(initialContent); }, [initialContent]);

  const handleChange = useCallback((html: string) => {
    setContent(html);
    setSaved(false);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const saved = await saveDiarySlot.mutateAsync({
          date: format(date, "yyyy-MM-dd"),
          slotKey: `slot-${slot.id}`,
          data: { timetableId, courseId: slot.courseId ?? undefined, content: html },
        });
        setDiaryEntryId(saved.id);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        // Refresh the diary-entry count used by the parent's "with content" stat
        queryClient.invalidateQueries({ queryKey: getGetDiaryEntriesQueryKey() });
      } catch {}
    }, 800);
  }, [date, slot.id, slot.courseId, timetableId, saveDiarySlot, queryClient]);

  const handleProgramInsert = useCallback((html: string) => {
    if (!html) return;
    if (insertRef.current?.isEmpty()) {
      insertRef.current.append(html);
    } else {
      setPendingInsertHtml(html);
    }
  }, []);

  const handleProgramPick = useCallback(async (registerId: number, item: ProgramItem) => {
    // Ensure the slot is saved before persisting the link so we have a diaryEntryId.
    let entryId = diaryEntryId;
    if (!entryId) {
      try {
        const saved = await saveDiarySlot.mutateAsync({
          date: format(date, "yyyy-MM-dd"),
          slotKey: `slot-${slot.id}`,
          data: { timetableId, courseId: slot.courseId ?? undefined, content },
        });
        entryId = saved.id;
        setDiaryEntryId(saved.id);
      } catch {
        return; // abort link if save fails
      }
    }
    try {
      await addLink.mutateAsync({ diaryEntryId: entryId, programRegisterId: registerId, programItemId: item.id });
    } catch {
      // link may already exist — silently ignore
    }
  }, [diaryEntryId, saveDiarySlot, date, slot.id, slot.courseId, timetableId, content, addLink]);

  const color = slot.courseColor ?? "#94a3b8";
  const label = dateLabel(date, weekLabel, slot.label);
  const dateStr = format(date, "d/MM/yyyy");

  return (
  <>
    <div
      ref={isToday && todayRef ? todayRef : null}
      className="border-b border-gray-100 last:border-0"
    >
      {/* Entry header */}
      <div className="flex items-center justify-between px-6 py-3 hover:bg-gray-50/50 transition-colors">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-1 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="text-left min-w-0 flex items-center gap-2 flex-wrap"
          >
            <span className="font-semibold text-sm" style={{ color }}>{label}</span>
            <span className="text-gray-400 text-sm">· {dateStr}</span>
            {slot.className ? (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                {slot.className}
              </span>
            ) : slot.classMissing ? (
              // Task #142: classId on this lesson points at a hard-deleted
              // class. Render a muted tag instead of a silent blank so the
              // reviewer / head teacher can see the broken reference.
              <span
                className="text-xs text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full font-medium"
                data-testid="lesson-deleted-class"
                title="The class this lesson pointed at no longer exists."
              >
                (deleted class)
              </span>
            ) : null}
            {slot.courseName ? (
              <span className="text-xs text-gray-500">{slot.courseName}</span>
            ) : slot.courseMissing ? (
              // Task #142: courseId on this lesson points at a hard-deleted
              // course. Same muted-tag treatment.
              <span
                className="text-xs text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full font-medium"
                data-testid="lesson-deleted-course"
                title="The course this lesson pointed at no longer exists."
              >
                (deleted course)
              </span>
            ) : null}
            {isToday && (
              <Badge className="text-xs bg-blue-600 text-white border-0 py-0">Today</Badge>
            )}
          </button>
          {saved && <span className="text-xs text-green-600 flex-shrink-0">✓ Saved</span>}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {hasActiveRegisters && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
              title="Pull from program"
              onClick={() => setShowProgramPicker(true)}
              data-write-action
            >
              <BookOpen className="h-3.5 w-3.5" />
              Program
            </Button>
          )}
          {pulledItems.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className={`h-7 gap-1.5 text-xs transition-colors ${
                allDeliveryLogged
                  ? "text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                  : "text-blue-600 hover:bg-blue-50"
              }`}
              title={allDeliveryLogged ? "Delivery logged — click to review or update" : "Log delivery status"}
              onClick={() => setShowDeliveryStatus(true)}
              data-write-action
            >
              {allDeliveryLogged ? (
                <CheckCircle2 className="h-3.5 w-3.5" />
              ) : (
                <ClipboardList className="h-3.5 w-3.5" />
              )}
              {persistedLinks.length > 1 && (
                <span className="text-xs font-medium tabular-nums">
                  {loggedCount}/{persistedLinks.length}
                </span>
              )}
              Delivery
            </Button>
          )}
          <Button
            variant="ghost" size="icon"
            className="h-7 w-7 text-gray-400 hover:text-gray-700"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-gray-700">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={() => onExportThis({ date, weekLabel, slot, content })}>
                <Download className="h-3.5 w-3.5 mr-2" />
                Export this lesson
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Editor + program tools */}
      {!collapsed && (
        <div className="px-6 pb-4">
          <RichEditor
            content={content}
            onChange={handleChange}
            placeholder={`Lesson plan for ${slot.className ?? slot.courseName ?? slot.label}…`}
            minHeight={100}
            insertRef={insertRef}
           data-write-action/>
        </div>
      )}
    </div>

    <ProgramItemPicker
      open={showProgramPicker}
      onClose={() => setShowProgramPicker(false)}
      onInsert={handleProgramInsert}
      onPickItem={handleProgramPick}
    />

    <AlertDialog
      open={pendingInsertHtml !== null}
      onOpenChange={(v) => { if (!v) setPendingInsertHtml(null); }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Add to existing content?</AlertDialogTitle>
          <AlertDialogDescription>
            This lesson plan already has content. Add the program item below the existing content, or replace it?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setPendingInsertHtml(null)}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (pendingInsertHtml) insertRef.current?.append(pendingInsertHtml);
              setPendingInsertHtml(null);
            }}
          >
            Add below
          </AlertDialogAction>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => {
              if (pendingInsertHtml) insertRef.current?.replace(pendingInsertHtml);
              setPendingInsertHtml(null);
            }}
          >
            Replace all
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <DeliveryStatusQuickLog
      open={showDeliveryStatus}
      onClose={() => setShowDeliveryStatus(false)}
      pulledItems={pulledItems}
      diaryEntryId={diaryEntryId}
      slotLabel={slot.label}
      slotClassId={slot.classId ?? null}
    />
  </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Lessons() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const todayRef = useRef<HTMLDivElement | null>(null);

  // Date range: start to end
  const todayStr = format(new Date(), "yyyy-MM-dd");

  // Parse any pre-filter params passed from the diary
  const searchParams = new URLSearchParams(search);
  const initClassId = searchParams.get("classId") ? Number(searchParams.get("classId")) : null;
  const initCourseId = searchParams.get("courseId") ? Number(searchParams.get("courseId")) : null;

  const [selectedTimetableId, setSelectedTimetableId] = useState<number | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<number | null>(initClassId);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(initCourseId);
  const [showOnlyWithContent, setShowOnlyWithContent] = useState(false);

  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState("");
  const [exportDateTo, setExportDateTo] = useState("");
  const [exportCourseId, setExportCourseId] = useState<number | null>(null);
  const [exportIncludeEmpty, setExportIncludeEmpty] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [popupBlocked, setPopupBlocked] = useState(false);

  const { toast } = useToast();
  const { activeTimetableId: diaryContextTimetableId } = useDiary();
  const { data: timetables, isLoading: isTimetablesLoading } = useListTimetables();
  // Prefer the user's explicit in-page selection, then fall back to the
  // DiaryContext active timetable (which already validates against archived
  // status), rather than blindly taking timetables[0] which may be archived.
  const activeTimetableId = selectedTimetableId ?? diaryContextTimetableId ?? null;

  const { data: timetableDetail, isLoading: isTimetableLoading } = useGetTimetable(
    activeTimetableId ?? 0,
    { query: { enabled: !!activeTimetableId } }
  );

  // Include archived courses/classes so historical timetables and lesson
  // entries still display the "(archived)" name for any year-end-archived
  // row. The active timetable settings picker uses the default hooks and
  // never surfaces archived rows.
  const { data: allCourses } = useListCourses({ includeArchived: "1" });
  const { data: allClasses } = useListClasses({ includeArchived: "1" });

  // Effective date range — always the full timetable span
  const effectiveStart = timetableDetail?.startDate ?? todayStr;
  const effectiveEnd = timetableDetail?.endDate ?? todayStr;

  const { data: diaryEntries, isLoading: isEntriesLoading } = useGetDiaryEntries(
    {
      timetableId: activeTimetableId ?? 0,
      startDate: effectiveStart,
      endDate: effectiveEnd,
    },
    { query: { enabled: !!activeTimetableId && !!timetableDetail } }
  );

  const { data: dailyOverrides } = useListDailyOverrides(
    {
      startDate: effectiveStart,
      endDate: effectiveEnd,
    },
    { query: { enabled: !!activeTimetableId && !!timetableDetail } }
  );

  const dailyOverrideByDate = useMemo(() => {
    const map = new Map<string, NonNullable<typeof dailyOverrides>[number]>();
    for (const override of dailyOverrides ?? []) {
      map.set(override.date, override);
    }
    return map;
  }, [dailyOverrides]);

  const dailyOverrideDates = useMemo(
    () => new Set((dailyOverrides ?? []).map((override) => override.date)),
    [dailyOverrides]
  );

  // Build a lookup: `date|slotKey` → content
  const entryMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of diaryEntries ?? []) {
      if (e.slotKey !== "day_notes" && e.content) {
        map.set(`${e.date}|${e.slotKey}`, e.content);
      }
    }
    return map;
  }, [diaryEntries]);

  // Maps diary slot key → diary_entry id so LessonEntry can hydrate its
  // persistent program links on mount (Task #199).
  const entryIdMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of diaryEntries ?? []) {
      if (e.slotKey !== "day_notes" && e.id) {
        map.set(`${e.date}|${e.slotKey}`, e.id);
      }
    }
    return map;
  }, [diaryEntries]);

  // Classes and courses that appear in the timetable slots
  const { timetableClassIds, timetableCourseIds } = useMemo(() => {
    const classIds = new Set<number>();
    const courseIds = new Set<number>();
    for (const week of timetableDetail?.weeks ?? []) {
      for (const slot of week.slots ?? []) {
        if (slot.classId) classIds.add(slot.classId);
        if (slot.courseId) courseIds.add(slot.courseId);
      }
    }
    return { timetableClassIds: classIds, timetableCourseIds: courseIds };
  }, [timetableDetail]);

  const filteredClasses = useMemo(
    () => (allClasses ?? []).filter((c) => timetableClassIds.has(c.id)),
    [allClasses, timetableClassIds]
  );

  // All courses in the active timetable — used by the export dialog regardless
  // of which class filter the teacher has set in the main Lessons view.
  const exportDialogCourses = useMemo(
    () => (allCourses ?? []).filter((c) => timetableCourseIds.has(c.id)),
    [allCourses, timetableCourseIds],
  );

  // Courses that appear in the selected class's slots (or all timetable courses if no class selected)
  const filteredCourses = useMemo(() => {
    if (!selectedClassId) {
      return (allCourses ?? []).filter((c) => timetableCourseIds.has(c.id));
    }
    const courseIds = new Set<number>();
    for (const week of timetableDetail?.weeks ?? []) {
      for (const slot of week.slots ?? []) {
        if (slot.classId === selectedClassId && slot.courseId) courseIds.add(slot.courseId);
      }
    }
    return (allCourses ?? []).filter((c) => courseIds.has(c.id));
  }, [allCourses, timetableDetail, selectedClassId, timetableCourseIds]);

  // Build sets of archived ids so the schedule below can suffix names with
  // "(archived)" for any historical lesson tied to a year-end-archived
  // class or course. The slot data itself comes from /timetables/{id} which
  // resolves names by id without any archived filter, so the suffix is the
  // only signal that the underlying row has been retired.
  const archivedClassIds = useMemo(
    () => new Set((allClasses ?? []).filter((c) => c.archived).map((c) => c.id)),
    [allClasses],
  );
  const archivedCourseIds = useMemo(
    () => new Set((allCourses ?? []).filter((c) => c.archived).map((c) => c.id)),
    [allCourses],
  );

  // Generate the lesson schedule for the date range
  const schedule = useMemo(() => {
    if (!timetableDetail || !effectiveStart) return [];

    const weeks = timetableDetail.weeks ?? [];
    const weekCount = weeks.length;
    if (weekCount === 0) return [];

    const timetableStart = new Date(timetableDetail.startDate + "T00:00:00");
    const start = new Date(effectiveStart + "T00:00:00");
    const end = new Date(effectiveEnd + "T00:00:00");
    const totalDays = differenceInCalendarDays(end, start) + 1;

    const items: Array<{
      date: Date;
      dateStr: string;
      weekLabel: string;
      slot: SlotShape;
      slotKey: string;
      content: string;
      diaryEntryId: number | null;
    }> = [];

    for (let i = 0; i < totalDays; i++) {
      const d = addDays(start, i);
      if (isWeekend(d)) continue;

      const dayName = getDayName(d);
      // Compute cycle week relative to timetable start
      const weekIdx = Math.max(0, differenceInCalendarWeeks(d, timetableStart, { weekStartsOn: 1 }));
      const cycleIdx = weekIdx % weekCount;
      const week = weeks[cycleIdx];

      const dateStr = format(d, "yyyy-MM-dd");
      const dailyOverride = dailyOverrideByDate.get(dateStr);

      const daySlots = dailyOverride
        ? dailyOverride.slots
            .filter((s) => s.slotType === "period")
            .filter((s) => !selectedClassId || s.classId === selectedClassId)
            .filter((s) => !selectedCourseId || s.courseId === selectedCourseId)
            .sort((a, b) => a.position - b.position)
            .map((s) => ({
              ...s,
              id: -Math.abs(s.position),
              day: dayName,
            }))
        : (week?.slots ?? [])
            .filter((s) => s.day === dayName && s.slotType === "period")
            .filter((s) => !selectedClassId || s.classId === selectedClassId)
            .filter((s) => !selectedCourseId || s.courseId === selectedCourseId)
            .sort((a, b) => a.position - b.position);

      for (const slot of daySlots) {
        const slotKey = dailyOverride ? `override-${slot.position}` : `slot-${slot.id}`;
        const content = entryMap.get(`${dateStr}|${slotKey}`) ?? "";
        // Suffix archived class/course names so the entry header makes it
        // visually obvious that this lesson belongs to a retired row.
        const enrichedSlot: SlotShape = {
          ...slot,
          className:
            slot.className && slot.classId && archivedClassIds.has(slot.classId)
              ? `${slot.className} (archived)`
              : slot.className,
          courseName:
            slot.courseName && slot.courseId && archivedCourseIds.has(slot.courseId)
              ? `${slot.courseName} (archived)`
              : slot.courseName,
          // Task #142: pass through the server's hard-deleted-FK flags so
          // LessonEntry can render a "(deleted course/class)" tag.
          courseMissing: (slot as { courseMissing?: boolean }).courseMissing,
          classMissing: (slot as { classMissing?: boolean }).classMissing,
        };
        items.push({
          date: d,
          dateStr,
          weekLabel: dailyOverride ? "Custom" : week?.weekLabel ?? String(weekIdx + 1),
          slot: enrichedSlot,
          slotKey,
          content,
          diaryEntryId: entryIdMap.get(`${dateStr}|${slotKey}`) ?? null,
        });
      }
    }

    return items;
  }, [
    timetableDetail,
    effectiveStart,
    effectiveEnd,
    selectedClassId,
    selectedCourseId,
    entryMap,
    entryIdMap,
    dailyOverrideByDate,
    archivedClassIds,
    archivedCourseIds,
  ]);

  // Detect whether the selected timetable has any period-type slots at all.
  // Used to distinguish "no periods configured yet" from "filters hide results"
  // or "date range too narrow" in the empty state below.
  const timetableHasPeriods =
    timetableDetail != null
      ? (timetableDetail.weeks ?? []).some((w) =>
          (w.slots ?? []).some((s) => s.slotType === "period")
        )
      : null; // null while still loading


  // Protected orphans: diary entries that have content but whose slotKey no longer
  // matches any slot in the current timetable structure (e.g. the slot was deleted
  // or the timetable was resaved before the intelligent-upsert fix was deployed).
  // These are surfaced in an amber "Protected existing content" section so no notes
  // are ever invisible to the teacher. No extra API call — uses already-fetched
  // diaryEntries vs the computed schedule.
  const orphanedItems = useMemo(() => {
    if (!diaryEntries?.length) return [];
    const scheduledKeys = new Set(schedule.map((i) => `${i.dateStr}|${i.slotKey}`));
    const orphaned = diaryEntries.filter((e) => {
      if (e.slotKey === "day_notes" || !e.content?.trim()) return false;

      // On customised days, the diary uses override-* slot keys. Do not rescue
      // old normal timetable slot-* entries as "protected" lesson content for
      // that date, because they are no longer part of the visible diary layout.
      if (dailyOverrideDates.has(e.date) && /^slot-\d+$/.test(e.slotKey)) return false;

      return !scheduledKeys.has(`${e.date}|${e.slotKey}`);
    });
    if (!orphaned.length) return [];

    const byDate = new Map<string, typeof orphaned>();
    for (const e of orphaned) {
      if (!byDate.has(e.date)) byDate.set(e.date, []);
      byDate.get(e.date)!.push(e);
    }

    const items: typeof schedule = [];
    for (const [dateStr, dayOrphans] of [...byDate.entries()].sort()) {
      if (isWeekend(new Date(dateStr + "T00:00:00"))) continue;
      const d = new Date(dateStr + "T00:00:00");
      dayOrphans.sort((a, b) => a.slotKey.localeCompare(b.slotKey));
      dayOrphans.forEach((e, idx) => {
        const slotId = parseInt(e.slotKey.replace("slot-", ""), 10);
        const orphanCourse = (allCourses ?? []).find((c) => c.id === e.courseId) ?? null;
        items.push({
          date: d,
          dateStr,
          weekLabel: "—",
          slot: {
            id: isNaN(slotId) ? -(idx + 1) : slotId,
            label: `Period ${idx + 1}`,
            position: 1000 + idx,
            courseId: e.courseId ?? null,
            courseName: orphanCourse?.name ?? null,
            courseColor: orphanCourse?.color ?? null,
            classId: null,
            className: null,
          },
          slotKey: e.slotKey,
          content: e.content ?? "",
          diaryEntryId: e.id ?? null,
        });
      });
    }
    return items;
  }, [diaryEntries, schedule, allCourses, dailyOverrideDates]);

  const pendingScrollToToday = useRef(false);

  const scrollToToday = () => {
    const tStart = timetableDetail?.startDate ?? null;
    const tEnd   = timetableDetail?.endDate   ?? null;
    if ((tStart && todayStr < tStart) || (tEnd && todayStr > tEnd)) {
      toast({ title: "Today is outside the selected timetable's date range." });
      return;
    }
    pendingScrollToToday.current = true;
    if (todayRef.current) {
      todayRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // Auto-scroll to today once when the schedule first renders and today is
  // within the displayed range.
  const hasAutoScrolled = useRef(false);
  useEffect(() => {
    if (hasAutoScrolled.current) return;
    if (!todayRef.current) return;
    if (todayStr < effectiveStart || todayStr > effectiveEnd) return;
    hasAutoScrolled.current = true;
    const timer = setTimeout(() => {
      todayRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
    return () => clearTimeout(timer);
  }, [schedule, effectiveStart, effectiveEnd, todayStr]);

  // Deferred scroll: fires after a range-snap moves today back into view
  useEffect(() => {
    if (!pendingScrollToToday.current || !todayRef.current) return;
    pendingScrollToToday.current = false;
    const timer = setTimeout(() => {
      todayRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
    return () => clearTimeout(timer);
  }, [schedule]);

  const handleOpenExportDialog = () => {
    setExportDateFrom(effectiveStart);
    setExportDateTo(effectiveEnd);
    // Default to the current Lessons subject filter (if one is active).
    setExportCourseId(selectedCourseId);
    setExportIncludeEmpty(false);
    setExportError(null);
    setPopupBlocked(false);
    setExportDialogOpen(true);
  };

  const handleDoExport = async () => {
    setExportError(null);
    setPopupBlocked(false);

    // Validate dates against the selected timetable's range
    const tStart = timetableDetail?.startDate;
    const tEnd   = timetableDetail?.endDate;
    if (tStart && exportDateFrom && exportDateFrom < tStart) {
      setExportError(`Start date is before the timetable start (${tStart}). Please adjust.`);
      return;
    }
    if (tEnd && exportDateTo && exportDateTo > tEnd) {
      setExportError(`End date is after the timetable end (${tEnd}). Please adjust.`);
      return;
    }
    if (exportDateFrom && exportDateTo && exportDateFrom > exportDateTo) {
      setExportError("Start date must be on or before end date.");
      return;
    }

    // Open the window SYNCHRONOUSLY during the click event so the browser
    // recognises it as user-initiated. Calling window.open() after an await
    // causes popup blockers to block it silently.
    const exportWindow = window.open("", "_blank");
    if (!exportWindow) {
      setPopupBlocked(true);
      return;
    }

    // Write a loading placeholder so the teacher sees something immediately.
    exportWindow.document.write(
      "<!DOCTYPE html><html><head><title>Preparing export…</title></head>" +
      "<body style=\"font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;" +
      "padding:40px;color:#555;font-size:15px\"><p>Preparing lesson export…</p></body></html>"
    );
    exportWindow.document.close();

    setExportLoading(true);
    try {
      const params = new URLSearchParams({ name: "Lesson Plans" });
      if (exportDateFrom)   params.set("dateFrom",     exportDateFrom);
      if (exportDateTo)     params.set("dateTo",       exportDateTo);
      if (exportCourseId)   params.set("courseId",     String(exportCourseId));
      if (exportIncludeEmpty) params.set("includeEmpty", "1");

      // customFetch adds the Authorization: Bearer token so Clerk auth works.
      const html = await customFetch<string>(
        `/api/exports/lessons.html?${params.toString()}`,
        { responseType: "text" },
      );

      exportWindow.document.open();
      exportWindow.document.write(html as string);
      exportWindow.document.close();
      setExportDialogOpen(false);
    } catch {
      // Write an error into the already-open window so the teacher knows what happened.
      exportWindow.document.open();
      exportWindow.document.write(
        "<!DOCTYPE html><html><head><title>Export failed</title></head>" +
        "<body style=\"font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;" +
        "padding:40px;color:#c00;font-size:15px\">" +
        "<p>Export failed. Please close this tab and try again.</p></body></html>"
      );
      exportWindow.document.close();
      setExportError("Export failed. Please try again.");
      // leave dialog open so user can retry
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportThis = (entry: { date: Date; weekLabel: string; slot: SlotShape; content: string }) => {
    const selectedTimetable = timetables?.find((t) => t.id === activeTimetableId);
    const html = buildExportHtml([entry], selectedTimetable?.name ?? "Lessons", entry.slot.className ?? entry.slot.courseName ?? null);
    const win = window.open();
    if (win) { win.document.write(html); win.document.close(); }
  };

  const isLoading = isTimetablesLoading || isTimetableLoading || isEntriesLoading;

  if (!isTimetablesLoading && (!timetables || timetables.length === 0)) {
    return (
      <div className="p-8 max-w-4xl mx-auto text-center mt-20">
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-12">
          <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Timetable Found</h2>
          <p className="text-gray-600 mb-6">Set up a timetable first to view lessons.</p>
          <Button onClick={() => setLocation("/app/timetable-settings")}>Set Up Timetable</Button>
        </div>
      </div>
    );
  }

  // requiresCourseSelection was previously true when courses existed but none was selected.
  // Removed: class-only and mixed timetables should show lessons without forcing a course
  // selection. Teachers can use the subject/class filter dropdowns to narrow if needed.
  const requiresCourseSelection = false;

  // Orphaned items always have content (filtered above), so include them in the count
  const withContent = schedule.filter((e) => e.content?.trim()).length + orphanedItems.length;
  // displaySchedule: apply the "with content only" filter over the schedule.
  const displaySchedule = useMemo(() => {
    return showOnlyWithContent
      ? schedule.filter((e) => !!e.content?.trim())
      : schedule;
  }, [showOnlyWithContent, schedule]);

  return (
    <>
    <div className="flex flex-col h-full bg-white">

      {/* ── Filter bar ── */}
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white shadow-sm">

        {/* Row 1: selects + export */}
        <div className="flex items-center gap-2.5 px-4 sm:px-6 py-2.5 border-b border-gray-100 flex-wrap">

          {/* Timetable */}
          <Select
            value={activeTimetableId ? String(activeTimetableId) : ""}
            onValueChange={(v) => {
              setSelectedTimetableId(Number(v));
              setSelectedClassId(null);
              setSelectedCourseId(null);
            }}
          >
            <SelectTrigger className="w-44 h-9 text-sm">
              <SelectValue placeholder="Timetable" />
            </SelectTrigger>
            <SelectContent>
              {(timetables ?? []).map((t) => (
                <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Class filter */}
          <Select
            value={selectedClassId ? String(selectedClassId) : "all"}
            onValueChange={(v) => {
              setSelectedClassId(v === "all" ? null : Number(v));
              setSelectedCourseId(null);
            }}
          >
            <SelectTrigger className="w-40 h-9 text-sm">
              <SelectValue placeholder="All classes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All classes</SelectItem>
              {filteredClasses.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Course filter */}
          {filteredCourses.length > 0 && (
            <Select
              value={selectedCourseId ? String(selectedCourseId) : "all"}
              onValueChange={(v) => setSelectedCourseId(v === "all" ? null : Number(v))}
            >
              <SelectTrigger className="w-44 h-9 text-sm">
                <SelectValue placeholder="All subjects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All subjects</SelectItem>
                {filteredCourses.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: c.color }} />
                      {c.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="ml-auto flex items-center gap-2">
            {/* Show filter: segmented toggle */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500 font-medium">Show:</span>
              <div className="flex rounded-md border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowOnlyWithContent(false)}
                  className={`px-3 h-8 text-xs font-medium transition-colors ${
                    !showOnlyWithContent
                      ? "bg-gray-900 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  All lessons
                </button>
                <button
                  type="button"
                  onClick={() => setShowOnlyWithContent(true)}
                  className={`px-3 h-8 text-xs font-medium border-l border-gray-200 transition-colors ${
                    showOnlyWithContent
                      ? "bg-gray-900 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  With content only
                </button>
              </div>
            </div>
            {/* Stats chip */}
            {schedule.length > 0 && (
              <span className="text-xs text-gray-400">
                {withContent} / {schedule.length} with content
              </span>
            )}

            {/* Today */}
            <Button variant="outline" size="sm" className="h-9 text-xs text-blue-600 border-blue-200 hover:bg-blue-50" onClick={scrollToToday}>
              Today
            </Button>

            {/* Export */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 gap-1.5 text-sm">
                  <Download className="h-3.5 w-3.5" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={handleOpenExportDialog}>
                  <Download className="h-3.5 w-3.5 mr-2" />
                  Export lesson plans…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-auto">
        {isLoading && (
          <div className="max-w-3xl mx-auto p-8 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i}>
                <Skeleton className="h-10 w-3/4 mb-2" />
                <Skeleton className="h-24 w-full" />
              </div>
            ))}
          </div>
        )}

        {!isLoading && schedule.length === 0 && orphanedItems.length === 0 && (
          <div className="py-24 text-center">
            <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            {timetableHasPeriods === false ? (
              <>
                <h3 className="text-lg font-semibold text-gray-700 mb-1">
                  No periods configured
                </h3>
                <p className="text-sm text-gray-500 max-w-sm mx-auto mb-4">
                  Your timetable exists, but no periods have been added yet.
                  Set up your timetable periods to start seeing lessons here.
                </p>
                <Button size="sm" onClick={() => setLocation("/app/timetable-settings")}>
                  Go to Timetable Settings
                </Button>
              </>
            ) : selectedClassId || selectedCourseId ? (
              <>
                <h3 className="text-lg font-semibold text-gray-700 mb-1">
                  No lessons match your filters
                </h3>
                <p className="text-sm text-gray-500 max-w-sm mx-auto mb-4">
                  No timetabled periods match the current filters. Try clearing
                  your filters.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelectedClassId(null);
                    setSelectedCourseId(null);
                  }}
                >
                  Clear filters
                </Button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-gray-700 mb-1">
                  No lessons found
                </h3>
                <p className="text-sm text-gray-500 max-w-sm mx-auto">
                  No timetabled periods found. Check that your timetable has
                  periods configured for these dates.
                </p>
              </>
            )}
          </div>
        )}

        {!isLoading && (schedule.length > 0 || orphanedItems.length > 0) && (
          <div className="max-w-4xl mx-auto divide-y divide-gray-100">
            {displaySchedule.length === 0 && (
              <div className="py-16 text-center">
                <p className="text-sm text-gray-500 mb-2">No lessons with notes in this range.</p>
                <button
                  className="text-xs text-primary hover:underline"
                  onClick={() => setShowOnlyWithContent(false)}
                >
                  Show all lessons
                </button>
              </div>
            )}
            {displaySchedule.map((entry) => (
              <LessonEntry
                key={`${entry.dateStr}-${entry.slot.id}`}
                date={entry.date}
                weekLabel={entry.weekLabel}
                slot={entry.slot}
                timetableId={activeTimetableId!}
                initialContent={entry.content}
                initialDiaryEntryId={entry.diaryEntryId}
                todayRef={todayRef}
                onExportThis={handleExportThis}
              />
            ))}

            {orphanedItems.length > 0 && (
              <div className="border-t-2 border-amber-300">
                <div className="px-4 sm:px-6 py-3 bg-amber-50 flex items-start gap-3">
                  <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Lesson notes from a previous timetable</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Some lesson notes are linked to an earlier version of your timetable. They are still saved
                      and shown here so nothing is lost. To edit them, open the diary for that day.
                    </p>
                  </div>
                </div>
                {orphanedItems.map((entry) => (
                  <LessonEntry
                    key={`protected-${entry.dateStr}-${entry.slot.id}`}
                    date={entry.date}
                    weekLabel={entry.weekLabel}
                    slot={entry.slot}
                    timetableId={activeTimetableId!}
                    initialContent={entry.content}
                    initialDiaryEntryId={entry.diaryEntryId}
                    onExportThis={handleExportThis}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>

    {/* Export dialog — date range picker for lesson-plan HTML export */}
    <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Export Lesson Plans</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">From</label>
            <input
              type="date"
              className="h-9 rounded-md border border-input px-3 text-sm"
              value={exportDateFrom}
              min={timetableDetail?.startDate}
              max={timetableDetail?.endDate}
              onChange={(e) => setExportDateFrom(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Until</label>
            <input
              type="date"
              className="h-9 rounded-md border border-input px-3 text-sm"
              value={exportDateTo}
              min={timetableDetail?.startDate}
              max={timetableDetail?.endDate}
              onChange={(e) => setExportDateTo(e.target.value)}
            />
          </div>
          {exportDialogCourses.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Subject</label>
              <Select
                value={exportCourseId ? String(exportCourseId) : "all"}
                onValueChange={(v) => setExportCourseId(v === "all" ? null : Number(v))}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="All subjects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All subjects</SelectItem>
                  {exportDialogCourses.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: c.color }} />
                        {c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Include</label>
            <div className="flex flex-col gap-1">
              {(
                [
                  { value: false, label: "With content only" },
                  { value: true,  label: "All scheduled periods" },
                ] as const
              ).map(({ value, label }) => (
                <label key={String(value)} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="radio"
                    name="exportIncludeEmpty"
                    checked={exportIncludeEmpty === value}
                    onChange={() => {
                      setExportIncludeEmpty(value);
                      // Auto-fill dates from the active timetable when the user
                      // switches to "All scheduled periods" and hasn't set a range yet.
                      if (value === true && timetableDetail) {
                        if (!exportDateFrom && timetableDetail.startDate) setExportDateFrom(timetableDetail.startDate);
                        if (!exportDateTo) {
                          // Timetables with no end date are ongoing — default to today.
                          const todayStr = new Date().toISOString().slice(0, 10);
                          setExportDateTo(timetableDetail.endDate ?? todayStr);
                        }
                      }
                    }}
                    className="accent-indigo-600"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Opens a print-ready page in a new tab. Use your browser's Print → Save as PDF.
          </p>
          {popupBlocked && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Popup was blocked. Please allow popups for ClassTrack and try again.
            </p>
          )}
          {exportError && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {exportError}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => setExportDialogOpen(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleDoExport} disabled={exportLoading}>
            {exportLoading ? "Exporting…" : "Export"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
